/**
 * Floater (PRD §7, §7.1, C10, C11, C16, C26): dock/undock, positioning, return to slot.
 */
import { findDock, makeRect } from './safe-area.js';
import { HEADER_HEIGHT } from './size-profile.js';

/**
 * Pure float eligibility (PRD §13 locked: playing + scrolled fully off + token held + clear spot +
 * density ≤ 30%). The clear spot and density come from findDock. C10 (seen in the article, playing as it
 * left, fully out) is guaranteed by the only caller, onScrolledOff(); this covers the rest.
 * @param {object} input
 * @returns {{allowed:boolean, reason:string}}
 */
export function isFloatAllowed(input) {
  const no = (reason) => ({ allowed: false, reason });
  if (!input.floatEnabled) return no('disabled');
  if (input.closed) return no('closed'); // FL-7
  if (!input.floatSize) return no('small_screen'); // narrow profile: no float
  if (input.isPhone && input.mobileFloat === 'off') return no('small_screen'); // reported as small_screen (2.0.5)
  if (!input.holdsToken) return no('token'); // MP-10
  if (input.fullscreenAdOpen) return no('interstitial'); // §7.1
  if (input.inputFocused) return no('input_focus'); // §7.1 step 3
  return { allowed: true, reason: '' };
}

/**
 * Create the floater for one player.
 * @param {object} deps
 * @param {Window} deps.win
 * @param {Document} deps.doc
 * @param {object} deps.refs ui refs
 * @param {object} deps.safeArea from createSafeArea
 * @param {Function} deps.getProfile () => profile name
 * @param {Function} deps.getFloatSize () => [w, h] video frame size
 * @param {string[]} deps.dockOrder config.floatDock
 * @param {Element} deps.article
 * @param {Function} deps.requestFrame requestAnimationFrame
 * @param {Function} deps.onDecision (decision) => void for debug and telemetry
 * @returns {object}
 */
export function createFloater(deps) {
  const { win, doc, refs } = deps;
  const state = { floating: false, dock: null, rect: null, padding: 0, scheduled: false, suspended: null };

  /** @returns {object} viewport rect */
  const viewport = () => makeRect(0, 0, win.innerWidth, win.innerHeight);

  /** @returns {object|null} article column rect */
  function column() {
    if (!deps.article) return null;
    const rect = deps.article.getBoundingClientRect();
    return { left: rect.left, top: rect.top, right: rect.right, bottom: rect.bottom };
  }

  /**
   * Compute a dock for the current page.
   * @returns {object} findDock result plus box rect
   */
  function computeDock() {
    const [width, height] = deps.getFloatSize();
    // The box is the video frame + the 32 px header with the close button + a 1 px border on each side.
    const boxWidth = width + 2;
    const boxHeight = height + HEADER_HEIGHT + 2;
    deps.safeArea.rescan([refs.player]);
    const obstacles = deps.safeArea.obstacles([refs.player, refs.slot]);
    state.lastObstacles = obstacles;
    const result = findDock({
      size: [boxWidth, boxHeight],
      viewport: viewport(),
      obstacles,
      dockOrder: deps.dockOrder,
      profileName: deps.getProfile(),
      column: column(),
    });
    // The close button's hit area reaches 12 px above the header: keep that on screen too.
    if (result.dock && result.rect.top < 12) return { dock: null, reason: 'collision' };
    return result;
  }

  /**
   * Pause and hide the float without ending it (interstitial open, or an inline ad under the dock).
   * @param {'interstitial'} reason
   */
  function suspend(reason) {
    if (state.suspended) return;
    state.suspended = reason;
    refs.player.style.visibility = 'hidden';
    deps.onDecision({ type: 'suspend', reason });
  }

  /** Show the float again and let the player resume. */
  function unsuspend() {
    if (!state.suspended) return;
    state.suspended = null;
    refs.player.style.removeProperty('visibility');
    deps.onDecision({ type: 'resume' });
  }

  /**
   * Position the player box at a dock rect.
   * @param {object} rect
   */
  function place(rect) {
    const [width, height] = deps.getFloatSize();
    const player = refs.player;
    if (!player.classList.contains('qsvp-floating')) state.inArticleWidth = player.style.width;
    player.classList.add('qsvp-floating');
    player.style.left = rect.left + 'px';
    player.style.top = rect.top + 'px';
    player.style.width = width + 2 + 'px'; // + the 1 px border on each side: the video frame is exactly width x height
    refs.stage.style.aspectRatio = width + '/' + height;
  }

  /**
   * C11 / SA-12: the close button must be on screen and unobstructed.
   * @returns {boolean}
   */
  function closeButtonClear() {
    const rect = refs.close.getBoundingClientRect();
    if (rect.width === 0 || rect.top < 0 || rect.left < 0 || rect.bottom > win.innerHeight || rect.right > win.innerWidth) {
      return false;
    }
    if (typeof doc.elementFromPoint !== 'function') return true;
    const hit = doc.elementFromPoint(rect.left + rect.width / 2, rect.top + rect.height / 2);
    return !hit || hit === refs.close || refs.close.contains(hit);
  }

  /** Mobile: pad the page so every line of text can scroll above the float (§7.1 step 3). */
  function addPadding() {
    if (deps.getProfile() !== 'phone') return;
    const [, height] = deps.getFloatSize();
    state.padding = height + HEADER_HEIGHT + 16;
    doc.body.style.paddingBottom = state.padding + 'px';
  }

  /** Remove the page padding. */
  function removePadding() {
    if (!state.padding) return;
    doc.body.style.removeProperty('padding-bottom');
    state.padding = 0;
  }

  const api = {
    state,
    /**
     * Try to float. Returns the decision; the player pauses in place when it is not allowed.
     * @returns {{floated:boolean, reason:string, dock?:string}}
     */
    float() {
      const result = computeDock();
      if (!result.dock) {
        deps.onDecision({ type: 'blocked', reason: result.reason, coverage: result.coverage, obstacles: state.lastObstacles });
        return { floated: false, reason: result.reason };
      }
      state.floating = true;
      state.dock = result.dock;
      state.rect = result.rect;
      place(result.rect);
      addPadding();
      if (!closeButtonClear()) {
        api.unfloat('collision');
        deps.onDecision({ type: 'blocked', reason: 'collision', detail: 'close_obstructed' });
        return { floated: false, reason: 'collision' };
      }
      deps.onDecision({
        type: 'float',
        dock: result.dock,
        rect: result.rect,
        coverage: result.coverage,
        gutter: result.gutter,
        obstacles: state.lastObstacles,
      });
      return { floated: true, reason: '', dock: result.dock };
    },

    /**
     * Re-check while floating (SA-7): keep the spot if clear, else move, else un-float.
     * @returns {boolean} still floating
     */
    recheck() {
      if (!state.floating) return false;
      if (deps.safeArea.isFullscreenAdOpen()) {
        suspend('interstitial'); // §7.1: pause and hide the float until the interstitial closes
        return true;
      }
      const result = computeDock();
      if (!result.dock) {
        api.unfloat('collision');
        deps.onDecision({ type: 'unfloat', reason: result.reason });
        return false;
      }
      if (result.dock !== state.dock || result.rect.top !== state.rect.top || result.rect.left !== state.rect.left) {
        state.dock = result.dock;
        state.rect = result.rect;
        place(result.rect);
        deps.onDecision({ type: 'move', dock: result.dock, rect: result.rect });
      }
      unsuspend();
      if (!closeButtonClear()) {
        api.unfloat('collision');
        deps.onDecision({ type: 'unfloat', reason: 'close_obstructed' });
        return false;
      }
      return true;
    },

    /**
     * Schedule a recheck on the next frame (rAF throttle, SA-7).
     * @param {Function} onUnfloat called when the recheck ends the float
     */
    scheduleRecheck(onUnfloat) {
      if (!state.floating || state.scheduled) return;
      state.scheduled = true;
      deps.requestFrame(() => {
        state.scheduled = false;
        if (state.floating && !api.recheck()) onUnfloat();
      });
    },

    /**
     * Return the player to its slot.
     * @param {string} reason returned|closed|finished|collision
     */
    unfloat(reason) {
      if (!state.floating) return;
      state.floating = false;
      state.dock = null;
      state.rect = null;
      state.suspended = null;
      const player = refs.player;
      player.classList.remove('qsvp-floating');
      for (const prop of ['left', 'top', 'width', 'visibility']) player.style.removeProperty(prop);
      if (state.inArticleWidth) player.style.width = state.inArticleWidth;
      refs.stage.style.removeProperty('aspect-ratio');
      removePadding();
      deps.onDecision({ type: 'unfloat', reason });
    },
  };
  return api;
}
